const ASSIGNMENT_LABELS = [
  "Midterm Essay",
  "Final Exam Review",
  'Writing Prompt – "A World Without Technology"',
  "Romeo and Juliet Analysis",
  "Poetry Analysis",
];

const SHARED_ASSIGNMENTS = [
  { id: 1, name: "Midterm Essay" },
  { id: 2, name: "Final Exam Review" },
  { id: 3, name: 'Writing Prompt – "A World Without Technology"' },
  { id: 4, name: "Romeo and Juliet Analysis" },
  { id: 5, name: "Poetry Analysis" },
];

// Students per class for class ranking
const CLASS_STUDENTS = {
  1: [
    {
      studentId: 1,
      firstName: "Alice",
      lastName: "Johnson",
      avgScore: 87,
      scores: { 1: 78, 2: 83, 3: 88, 4: 91, 5: 93 },
    },
    {
      studentId: 2,
      firstName: "Brian",
      lastName: "Kim",
      avgScore: 72,
      scores: { 1: 71, 2: 73, 3: 70, 4: 73, 5: 72 },
    },
  ],
  2: [
    {
      studentId: 3,
      firstName: "Carlos",
      lastName: "Rivera",
      avgScore: 55,
      scores: { 1: 60, 2: 57, 3: 54, 4: 52, 5: 50 },
    },
    {
      studentId: 4,
      firstName: "Diana",
      lastName: "Moore",
      avgScore: 84,
      scores: { 1: 80, 2: 82, 3: 84, 4: 86, 5: 88 },
    },
  ],
  3: [
    {
      studentId: 5,
      firstName: "Ethan",
      lastName: "Davis",
      avgScore: 71,
      scores: { 1: 70, 2: 71, 3: 70, 4: 72, 5: 71 },
    },
    {
      studentId: 6,
      firstName: "Fiona",
      lastName: "Zhang",
      avgScore: 63,
      scores: { 1: 68, 2: 66, 3: 64, 4: 62, 5: 60 },
    },
  ],
};

function makeTable(scores) {
  const dates = [
    "Jan 15, 2026",
    "Jan 22, 2026",
    "Jan 29, 2026",
    "Feb 5, 2026",
    "Feb 12, 2026",
  ];
  const classAvgs = [70, 71, 71, 72, 72];
  return ASSIGNMENT_LABELS.map((name, i) => ({
    date: dates[i],
    assignmentId: i + 1,
    assignmentName: name,
    studentScore: scores[i],
    classAvg: classAvgs[i],
  }));
}

function makeRanking(studentId, classId) {
  const classmates = CLASS_STUDENTS[classId] || CLASS_STUDENTS[1];
  const sorted = [...classmates].sort((a, b) => b.avgScore - a.avgScore);
  const rank = sorted.findIndex((s) => s.studentId === studentId) + 1;
  const percentile =
    classmates.length === 0
      ? 0
      : Math.round(((classmates.length - rank) / classmates.length) * 100);
  return {
    rank,
    percentile,
    totalStudents: classmates.length,
    assignments: SHARED_ASSIGNMENTS,
    data: classmates.map((s) => ({
      ...s,
      isCurrentStudent: s.studentId === studentId,
    })),
  };
}

export const metricsStudentsDemoData = {
  1: {
    studentName: "Alice Johnson",
    classId: 1,
    progressChart: {
      labels: ASSIGNMENT_LABELS,
      datasets: [
        { label: "Student", data: [78, 83, 88, 91, 93] },
        { label: "Class Avg", data: [70, 71, 71, 72, 72] },
      ],
      table: makeTable([78, 83, 88, 91, 93]),
    },
    classRanking: makeRanking(1, 1),
    insights: {
      overallSummary:
        "Alice is the top performer in the class with a clear upward trajectory. Her thesis development and evidence integration are well above average. Deepening counterargument engagement would help her reach the next level.",
      skillsTrajectory: [
        {
          status: "Improving",
          skillName: "Thesis Development",
          description:
            "Thesis statements are increasingly sophisticated and well-argued.",
        },
        {
          status: "Improving",
          skillName: "Evidence Integration",
          description:
            "Sources are well-chosen and directly support the central argument.",
        },
        {
          status: "Stable",
          skillName: "Academic Tone",
          description:
            "Consistently maintains an appropriate and confident academic register.",
        },
        {
          status: "Improving",
          skillName: "Paragraph Transitions",
          description:
            "Transitions are smooth and significantly strengthen essay coherence.",
        },
        {
          status: "Stable",
          skillName: "Counterarguments",
          description: "Present but could be more nuanced and deeply engaged.",
        },
      ],
    },
  },
  2: {
    studentName: "Brian Kim",
    classId: 1,
    progressChart: {
      labels: ASSIGNMENT_LABELS,
      datasets: [
        { label: "Student", data: [71, 73, 70, 73, 72] },
        { label: "Class Avg", data: [70, 71, 71, 72, 72] },
      ],
      table: makeTable([71, 73, 70, 73, 72]),
    },
    classRanking: makeRanking(2, 1),
    insights: {
      overallSummary:
        "Brian's performance is reliably close to the class average with a stable trajectory. He meets expectations consistently but rarely exceeds them. Targeted feedback on analytical depth and evidence variety could help him break out of this plateau.",
      skillsTrajectory: [
        {
          status: "Stable",
          skillName: "Thesis Development",
          description: "Thesis statements reliably meet basic expectations.",
        },
        {
          status: "Stable",
          skillName: "Academic Tone",
          description:
            "Tone is consistently appropriate throughout all submissions.",
        },
        {
          status: "Stable",
          skillName: "Evidence Integration",
          description:
            "Evidence is present but often surface-level without deeper analysis.",
        },
        {
          status: "Stable",
          skillName: "Paragraph Transitions",
          description:
            "Transitions are functional but do not enhance the flow of ideas.",
        },
        {
          status: "Improving",
          skillName: "Counterarguments",
          description:
            "Recent assignments show slightly more engagement with opposing views.",
        },
      ],
    },
  },
  3: {
    studentName: "Carlos Rivera",
    classId: 2,
    progressChart: {
      labels: ASSIGNMENT_LABELS,
      datasets: [
        { label: "Student", data: [60, 57, 54, 52, 50] },
        { label: "Class Avg", data: [70, 71, 71, 72, 72] },
      ],
      table: makeTable([60, 57, 54, 52, 50]),
    },
    classRanking: makeRanking(3, 2),
    insights: {
      overallSummary:
        "Carlos is showing a persistent downward trend that requires immediate attention. Gaps in thesis construction, evidence use, and argument structure are compounding. One-on-one support and structured writing scaffolding are strongly recommended.",
      skillsTrajectory: [
        {
          status: "Declining",
          skillName: "Thesis Development",
          description:
            "Thesis statements are becoming increasingly vague and underdeveloped.",
        },
        {
          status: "Declining",
          skillName: "Evidence Integration",
          description:
            "Evidence is used infrequently and improperly cited in recent work.",
        },
        {
          status: "Declining",
          skillName: "Counterarguments",
          description:
            "No meaningful engagement with opposing viewpoints in recent submissions.",
        },
        {
          status: "Stable",
          skillName: "Academic Tone",
          description:
            "Basic academic tone is maintained but does not compensate for structural weaknesses.",
        },
        {
          status: "Declining",
          skillName: "Paragraph Transitions",
          description:
            "Paragraph structure and transitions are consistently weak.",
        },
      ],
    },
  },
  4: {
    studentName: "Diana Moore",
    classId: 2,
    progressChart: {
      labels: ASSIGNMENT_LABELS,
      datasets: [
        { label: "Student", data: [80, 82, 84, 86, 88] },
        { label: "Class Avg", data: [70, 71, 71, 72, 72] },
      ],
      table: makeTable([80, 82, 84, 86, 88]),
    },
    classRanking: makeRanking(4, 2),
    insights: {
      overallSummary:
        "Diana continues to demonstrate strong and steady academic growth. Her evidence integration and academic tone are consistently above class average. More varied source types and deeper counterargument development would help her reach the top tier.",
      skillsTrajectory: [
        {
          status: "Improving",
          skillName: "Evidence Integration",
          description:
            "Evidence is well-chosen and directly supports the central claims.",
        },
        {
          status: "Stable",
          skillName: "Academic Tone",
          description:
            "Maintains a strong academic register throughout all essays.",
        },
        {
          status: "Improving",
          skillName: "Thesis Development",
          description: "Thesis statements are consistently strong and clear.",
        },
        {
          status: "Improving",
          skillName: "Paragraph Transitions",
          description:
            "Transitions flow naturally and enhance essay coherence.",
        },
        {
          status: "Stable",
          skillName: "Counterarguments",
          description: "Acknowledged but lack depth and engagement.",
        },
      ],
    },
  },
  5: {
    studentName: "Ethan Davis",
    classId: 3,
    progressChart: {
      labels: ASSIGNMENT_LABELS,
      datasets: [
        { label: "Student", data: [70, 71, 70, 72, 71] },
        { label: "Class Avg", data: [70, 71, 71, 72, 72] },
      ],
      table: makeTable([70, 71, 70, 72, 71]),
    },
    classRanking: makeRanking(5, 3),
    insights: {
      overallSummary:
        "Ethan performs consistently at the class average with minimal variation. He meets expectations reliably across all assignments. Focused coaching on argument depth and a more assertive analytical voice could help him move meaningfully above average.",
      skillsTrajectory: [
        {
          status: "Stable",
          skillName: "Thesis Development",
          description: "Thesis statements meet basic expectations reliably.",
        },
        {
          status: "Stable",
          skillName: "Academic Tone",
          description: "Tone is consistently appropriate and professional.",
        },
        {
          status: "Stable",
          skillName: "Evidence Integration",
          description:
            "Evidence is present but rarely analyzed beyond the surface level.",
        },
        {
          status: "Stable",
          skillName: "Paragraph Transitions",
          description: "Transitions are adequate but do not elevate the essay.",
        },
        {
          status: "Improving",
          skillName: "Counterarguments",
          description: "Slight improvement in acknowledging opposing views.",
        },
      ],
    },
  },
  6: {
    studentName: "Fiona Zhang",
    classId: 3,
    progressChart: {
      labels: ASSIGNMENT_LABELS,
      datasets: [
        { label: "Student", data: [68, 66, 64, 62, 60] },
        { label: "Class Avg", data: [70, 71, 71, 72, 72] },
      ],
      table: makeTable([68, 66, 64, 62, 60]),
    },
    classRanking: makeRanking(6, 3),
    insights: {
      overallSummary:
        "Fiona is showing a gradual decline relative to the class average. Her writing fundamentals are intact but argument development and evidence use have weakened over the semester. Early targeted intervention on structural writing skills is recommended.",
      skillsTrajectory: [
        {
          status: "Declining",
          skillName: "Thesis Development",
          description:
            "Thesis statements have become progressively less focused.",
        },
        {
          status: "Declining",
          skillName: "Evidence Integration",
          description:
            "Evidence is used less frequently and with less precision over time.",
        },
        {
          status: "Stable",
          skillName: "Academic Tone",
          description: "Tone remains adequate throughout submissions.",
        },
        {
          status: "Declining",
          skillName: "Counterarguments",
          description:
            "Engagement with counterarguments has diminished significantly.",
        },
        {
          status: "Stable",
          skillName: "Paragraph Transitions",
          description: "Basic transitions are present but underdeveloped.",
        },
      ],
    },
  },
};

export const metricsStudentDemoData = metricsStudentsDemoData[1];
